"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";

export default function NotFound() {
  const router = useRouter();
  const [isVisible, setIsVisible] = useState(false);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    setIsVisible(true);

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, []);

  useEffect(() => {
    if (countdown === 0) {
      router.push("/");
    }
  }, [countdown, router]);

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#0a0a0a] px-6 overflow-hidden font-mono">

      {/* Grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Ambient glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 20% 60%, rgba(99,102,241,0.08) 0%, transparent 70%), radial-gradient(ellipse 40% 40% at 80% 20%, rgba(236,72,153,0.06) 0%, transparent 60%)",
        }}
      />

      {/* Large background 404 text */}
      <span
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 select-none pointer-events-none font-black text-transparent whitespace-nowrap"
        style={{
          fontSize: "40vw",
          WebkitTextStroke: "1px rgba(255,255,255,0.025)",
          fontFamily: "Georgia, serif",
        }}
      >
        404
      </span>

      {/* Card */}
      <div
        className={`relative z-10 w-full max-w-md transition-all duration-700 ease-out ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        {/* Eyebrow */}
        <div className="flex items-center gap-3 mb-7">
          <div
            className="flex-1 h-px"
            style={{ background: "linear-gradient(to right, transparent, rgba(255,255,255,0.12), transparent)" }}
          />
          <span className="text-[10px] tracking-[0.2em] uppercase text-white/30">
            Error · Page Not Found
          </span>
          <div
            className="flex-1 h-px"
            style={{ background: "linear-gradient(to right, transparent, rgba(255,255,255,0.12), transparent)" }}
          />
        </div>

        {/* Headline */}
        <h1
          className="font-black text-white leading-none tracking-tight mb-2"
          style={{ fontSize: "clamp(72px, 18vw, 120px)", fontFamily: "Georgia, serif" }}
        >
          404
          <span
            className="block italic text-transparent"
            style={{
              fontSize: "0.9em",
              WebkitTextStroke: "1px rgba(255,255,255,0.18)",
            }}
          >
            Not found
          </span>
        </h1>

        {/* Divider */}
        <div className="w-10 h-px bg-white/15 my-7" />

        {/* Description */}
        <p className="text-[13px] font-light text-white/40 leading-relaxed mb-9 max-w-[340px]">
          The page you're looking for has wandered off the map.
          We'll redirect you to the dashboard shortly.
        </p>

        {/* Button */}
        <div className="mb-10">
          <Link
            href="/"
            className="flex items-center justify-between w-full px-5 py-4 bg-white text-[#0a0a0a] text-[11px] tracking-[0.1em] uppercase rounded-sm transition-all duration-200 hover:bg-white/90 hover:translate-x-1 active:scale-[0.99]"
          >
            <span>Back to Home</span>
            <span className="opacity-50 text-sm">→</span>
          </Link>
        </div>

        {/* Countdown */}
        <span className="text-[10px] tracking-[0.15em] uppercase text-white/20">
          Redirecting to Home
        </span>
      </div>
    </div>
  );
}